import React, { useState } from 'react';
import {
  Box, Chip, Stack, Button, IconButton, Avatar, CircularProgress, Pagination,
  Tooltip, Typography, Card, CardContent, CardActions, Grid, useTheme
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'; // Import for AI functionality
import { booksAPI } from '../../services/api';

const BooksList = ({ 
  books, 
  loading, 
  totalItems,
  page,
  limit,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onBookClick,
  onTogglePublishStatus,
  onDeleteBook,
  isMobile = false
}) => {
  const theme = useTheme();

  // Add state to track books that have had their similar books count fetched
  const [similarBooksCounts, setSimilarBooksCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState({});
  const [findingSimilar, setFindingSimilar] = useState({}); // Add state for AI finding process

  // Function to fetch similar books count for a specific book
  const fetchSimilarBooksCount = async (bookId) => {
    if (similarBooksCounts[bookId] !== undefined) return; // Already fetched
    
    setLoadingCounts(prev => ({ ...prev, [bookId]: true }));
    
    try {
      const response = await booksAPI.getSimilarBooks(bookId);
      const count = response.data?.similarBooks?.length || 0;
      
      setSimilarBooksCounts(prev => ({ 
        ...prev, 
        [bookId]: count 
      }));
    } catch (error) {
      console.error(`Error fetching similar books for book ${bookId}:`, error);
      // Set to 0 on error to indicate it's been attempted
      setSimilarBooksCounts(prev => ({ ...prev, [bookId]: 0 }));
    } finally {
      setLoadingCounts(prev => ({ ...prev, [bookId]: false }));
    }
  };

  // NEW: Add function to find similar books using AI
  const findSimilarBooksWithAI = async (bookId) => {
    setFindingSimilar(prev => ({ ...prev, [bookId]: true }));
    
    try {
      // Call the AI-powered similar books finder
      const response = await booksAPI.findSimilarBooks(bookId);
      
      // After finding similar books, get the updated count
      const updatedResponse = await booksAPI.getSimilarBooks(bookId);
      const newCount = updatedResponse.data?.similarBooks?.length || 0;
      
      // Update state with the new count
      setSimilarBooksCounts(prev => ({ 
        ...prev, 
        [bookId]: newCount 
      }));
      
      return newCount;
    } catch (error) {
      console.error(`Error finding similar books for book ${bookId} with AI:`, error);
      return null;
    } finally {
      setFindingSimilar(prev => ({ ...prev, [bookId]: false }));
    }
  };

  // Function to get thumbnail URL or use placeholder
  const getThumbnail = (book) => {
    return book.thumbnail || `https://placehold.co/80x120?text=${encodeURIComponent(book.title.charAt(0))}`;
  };

  // DataGrid column definitions with responsive adjustments
  const columns = [
    // Thumbnail column - added before the cover column
    { 
      field: 'thumbnail',
      headerName: 'Thumbnail',
      width: isMobile ? 60 : 80,
      renderCell: (params) => (
        <Avatar
          variant="rounded"
          src={getThumbnail(params.row)}
          alt={`${params.row.title} thumbnail`}
          sx={{ 
            width: 40, 
            height: 60, 
            borderRadius: 1,
            '& img': { objectFit: 'cover' }
          }}
          imgProps={{
            onError: (e) => {
              e.target.onerror = null;
              e.target.src = `https://placehold.co/40x60?text=${encodeURIComponent(params.row.title.charAt(0))}`;
            }
          }}
        />
      ),
      sortable: false,
      filterable: false
    },
    { 
      field: 'cover',
      headerName: 'Cover',
      width: isMobile ? 60 : 80,
      renderCell: (params) => (
        <Avatar
          variant="rounded"
          src={params.row.cover}
          alt={params.row.title}
          sx={{ 
            width: 50, 
            height: 75, 
            borderRadius: 1,
            '& img': { objectFit: 'cover' }
          }}
          imgProps={{
            onError: (e) => {
              e.target.onerror = null;
              e.target.src = 'https://placehold.co/50x75?text=No+Cover';
            }
          }}
        />
      ),
      sortable: false,
      filterable: false
    },
    { 
      field: 'title', 
      headerName: 'Title', 
      width: isMobile ? 150 : 200,
      renderCell: (params) => (
        <Box sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main' }} 
             onClick={() => onBookClick(params.row)}>
          {params.value}
        </Box>
      )
    },
    { field: 'author', headerName: 'Author', width: isMobile ? 150 : 200 },
    { 
      field: 'audioUrls',
      headerName: 'Audio',
      width: 100,
      renderCell: (params) => {
        const audioCount = params.value ? Object.keys(params.value).length : 0;
        
        let chipColor = "default";
        if (audioCount >= 3) {
          chipColor = "success";
        } else if (audioCount > 0) {
          chipColor = "primary";
        }
        
        return (
          <Tooltip title={`${audioCount} audio version${audioCount !== 1 ? 's' : ''}`}>
            <Chip
              icon={<AudioFileIcon fontSize="small" />}
              label={audioCount}
              size="small"
              color={chipColor}
              variant={audioCount > 0 ? "filled" : "outlined"}
            />
          </Tooltip>
        );
      },
      sortable: false
    },
    { 
      field: 'textUrls',
      headerName: 'Text',
      width: 100,
      renderCell: (params) => {
        const textCount = params.value ? Object.keys(params.value).length : 0;
        
        let chipColor = "default";
        if (textCount >= 3) {
          chipColor = "success";
        } else if (textCount > 0) {
          chipColor = "primary";
        }
        
        return (
          <Tooltip title={`${textCount} text version${textCount !== 1 ? 's' : ''}`}>
            <Chip
              icon={<TextSnippetIcon fontSize="small" />}
              label={textCount}
              size="small"
              color={chipColor}
              variant={textCount > 0 ? "filled" : "outlined"}
            />
          </Tooltip>
        );
      },
      sortable: false
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 150,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={params.value === 'published' ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'similarBooks',
      headerName: 'Similar Books',
      width: 150,
      renderCell: (params) => {
        const bookId = params.row.id;
        if (loadingCounts[bookId]) {
          return (
            <Button
              size="small"
              variant="outlined"
              disabled={true}
              startIcon={<CircularProgress size={16} />}
            >
              Loading...
            </Button>
          );
        }
        
        if (findingSimilar[bookId]) {
          return (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              disabled={true}
              startIcon={<CircularProgress size={16} color="secondary" />}
            >
              Finding with AI...
            </Button>
          );
        }
        
        if (similarBooksCounts[bookId] === undefined) {
          return (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                fetchSimilarBooksCount(bookId);
              }}
              startIcon={<CompareArrowsIcon />}
            >
              Show Similar
            </Button>
          );
        }
        
        if (similarBooksCounts[bookId] === 0) {
          return (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={(e) => {
                e.stopPropagation();
                findSimilarBooksWithAI(bookId);
              }}
              startIcon={<AutoAwesomeIcon />}
            >
              Find with AI
            </Button>
          );
        }
        
        return (
          <Chip 
            label={`${similarBooksCounts[bookId]} similar`}
            color="info"
            size="small"
            icon={<CompareArrowsIcon />}
          />
        );
      },
      sortable: false
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            color={params.row.status === 'published' ? 'warning' : 'success'}
            startIcon={params.row.status === 'published' ? <UnpublishedIcon /> : <PublishIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onTogglePublishStatus(params.row);
            }}
          >
            {params.row.status === 'published' ? 'Unpublish' : 'Publish'}
          </Button>
          <IconButton onClick={(e) => {
            e.stopPropagation();
            onDeleteBook(params.row);
          }}>
            <DeleteIcon />
          </IconButton>
        </Stack>
      )
    }
  ];

  // Mobile card view for books
  const renderMobileView = () => (
    <Grid container spacing={2} sx={{ p: 1, width: '100%', m: 0 }}>
      {books.map((book) => (
        <Grid item xs={12} key={book.id}>
          <Card 
            sx={{ 
              display: 'flex', 
              width: '100%',
              maxWidth: '100%',
              cursor: 'pointer',
              '&:hover': { boxShadow: 3 }
            }}
            onClick={() => onBookClick(book)}
          >
            {/* Display both thumbnail and cover side by side */}
            <Box sx={{ display: 'flex', m: 1 }}>
              {/* Thumbnail image */}
              <Avatar
                variant="rounded"
                src={getThumbnail(book)}
                alt={`${book.title} thumbnail`}
                sx={{ 
                  width: { xs: 40, sm: 50 }, 
                  height: { xs: 60, sm: 75 }, 
                  borderRadius: 1,
                  mr: 1
                }}
                imgProps={{
                  onError: (e) => {
                    e.target.onerror = null;
                    e.target.src = `https://placehold.co/40x60?text=${encodeURIComponent(book.title.charAt(0))}`;
                  }
                }}
              />
              
              {/* Cover image */}
              <Avatar
                variant="rounded"
                src={book.cover}
                alt={book.title}
                sx={{ 
                  width: { xs: 60, sm: 80 }, 
                  height: { xs: 90, sm: 120 }, 
                  borderRadius: 1
                }}
                imgProps={{
                  onError: (e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://placehold.co/80x120?text=No+Cover';
                  }
                }}
              />
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              flexGrow: 1,
              // Adjust width to accommodate both images
              width: 'calc(100% - 150px)'
            }}>
              <CardContent sx={{ flex: '1 0 auto', pb: 0 }}>
                <Typography variant="subtitle1" component="div" noWrap>
                  {book.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {book.author}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip 
                    label={book.status} 
                    color={book.status === 'published' ? 'success' : 'default'}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  
                  {book.audioUrls && (
                    <Chip
                      icon={<AudioFileIcon fontSize="small" />}
                      label={book.audioUrls ? Object.keys(book.audioUrls).length : 0}
                      size="small"
                      color={getChipColor(book.audioUrls ? Object.keys(book.audioUrls).length : 0)}
                      variant={(book.audioUrls && Object.keys(book.audioUrls).length > 0) ? "filled" : "outlined"}
                    />
                  )}
                  
                  {book.textUrls && (
                    <Chip
                      icon={<TextSnippetIcon fontSize="small" />}
                      label={book.textUrls ? Object.keys(book.textUrls).length : 0}
                      size="small"
                      color={getChipColor(book.textUrls ? Object.keys(book.textUrls).length : 0)}
                      variant={(book.textUrls && Object.keys(book.textUrls).length > 0) ? "filled" : "outlined"}
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
              </CardContent>
              <CardActions sx={{ flexWrap: 'wrap', gap: 1, p: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color={book.status === 'published' ? 'warning' : 'success'}
                  startIcon={book.status === 'published' ? <UnpublishedIcon /> : <PublishIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTogglePublishStatus(book);
                  }}
                  sx={{ 
                    fontSize: { xs: '0.7rem', sm: '0.8rem' }, 
                    minWidth: 0, 
                    px: { xs: 1, sm: 2 } 
                  }}
                >
                  {book.status === 'published' ? 'Unpublish' : 'Publish'}
                </Button>
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteBook(book);
                  }}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  // Helper function to determine chip color based on count
  const getChipColor = (count) => {
    if (count >= 3) return "success";
    if (count > 0) return "primary";
    return "default";
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Render either mobile view or DataGrid based on screen size
  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {isMobile ? (
        renderMobileView()
      ) : (
        <Box sx={{ height: 500, width: '100%', maxWidth: '100%', marginTop: 2 }}>
          <DataGrid 
            rows={books} 
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { 
                  pageSize: limit,
                  page: page - 1 
                },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            onPaginationModelChange={(model) => {
              if (model.pageSize !== limit) {
                onPageSizeChange(model.pageSize);
              } else if (model.page + 1 !== page) {
                onPageChange(null, model.page + 1);
              }
            }}
            rowCount={totalItems}
            paginationMode="server"
            loading={loading}
            disableRowSelectionOnClick
            onRowClick={(params) => onBookClick(params.row)}
          />
        </Box>
      )}
      
      <Box display="flex" justifyContent="center" mt={2} mb={2}>
        <Pagination 
          count={totalPages} 
          page={page} 
          onChange={onPageChange} 
          color="primary" 
          size={isMobile ? "small" : "medium"}
          siblingCount={isMobile ? 0 : 1}
        />
      </Box>
    </Box>
  );
};

export default BooksList;
